import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { usePlantsList } from "../../api/userInfo";
import { useDempStore } from "../../context/stores";
import { useTranslation } from "react-i18next";
import { useSubCompany } from "../../api/company";
import { cn } from "src/lib/utils";
import { api as axios } from "../../shared/axios";
import { API_URL } from "../../shared/config";

// Reusable Accordion Component for hierarchical company selection
const CompanyAccordionItem = ({
  parentId = null,
  level = 0,
  selectedCompanies,
  onSelectionChange,
  rootData = null,
  searchQuery = "",
  allowMultipleSelection = true,
  hierarchicalSelection = true,
}) => {
  // Use a Map to track expanded state for each company individually
  const [expandedItems, setExpandedItems] = useState(new Map());

  // For root level (level 0), use rootData (plantsList), otherwise use API
  // Memoize the enabled option to prevent infinite re-renders
  const shouldFetch = parentId !== null;
  const { subCompany: children, isLoadingSubCompany } = useSubCompany(
    parentId,
    { enabled: shouldFetch }
  );

  // For root level companies, we need to track which ones might have subsidiaries
  // and potentially fetch their data when needed
  const [subsidiaryDataCache, setSubsidiaryDataCache] = useState(new Map());

  // Helper function to get the correct subsidiary data for a company
  const getSubsidiaryData = useCallback(
    (company) => {
      if (level === 0) {
        // For root level, check cache first, then company.subsidiary
        const cachedData = subsidiaryDataCache.get(company.id);
        const finalData = cachedData || company.subsidiary;
        return finalData;
      } else {
        // For deeper levels, use company.subsidiary directly
        return company.subsidiary;
      }
    },
    [level, subsidiaryDataCache]
  );

  // Debug logging to check API data structure
  useEffect(() => {
    if (children && parentId) {
      // console.log(`🔍 DEBUG: API data for parent ${parentId}:`, children);
      // console.log(`🔍 DEBUG: children.subsidiary:`, children.subsidiary);

      // Cache the subsidiary data for this parent
      if (children.subsidiary && Array.isArray(children.subsidiary)) {
        // console.log(
        //   `🔍 DEBUG: Caching subsidiary data for parent ${parentId}:`,
        //   children.subsidiary
        // );
        setSubsidiaryDataCache((prev) => {
          const newCache = new Map(prev);
          newCache.set(parentId, children.subsidiary);
          // console.log(`🔍 DEBUG: Updated cache:`, newCache);
          return newCache;
        });
      }
    }
  }, [children, parentId]);

  // Helper function to recursively get all descendant IDs from a company
  const getAllDescendantIds = useCallback(
    (company, allCompaniesData = new Map()) => {
      const descendantIds = [];

      // Add the company's data to our tracking map
      allCompaniesData.set(company.id, company);

      // Use the subsidiary data directly from the company object
      // (this function is called with the correctly structured company object)
      const subsidiaryData = company.subsidiary;

      // If company has subsidiary data, recursively get their IDs
      if (subsidiaryData && Array.isArray(subsidiaryData)) {
        subsidiaryData.forEach((child) => {
          descendantIds.push(child.id);
          allCompaniesData.set(child.id, child);
          // Recursively get descendants of this child
          const childDescendants = getAllDescendantIds(child, allCompaniesData);
          descendantIds.push(...childDescendants);
        });
      }

      return descendantIds;
    },
    []
  );

  // Helper function to check if a parent has all children selected
  const areAllChildrenSelected = useCallback(
    (company) => {
      const subsidiaryData = getSubsidiaryData(company);

      if (!subsidiaryData || !Array.isArray(subsidiaryData)) {
        return true; // No children, so "all" are selected
      }

      return subsidiaryData.every((child) => {
        const isChildSelected = selectedCompanies.has(child.id);
        const areChildChildrenSelected = areAllChildrenSelected(child);
        return isChildSelected && areChildChildrenSelected;
      });
    },
    [selectedCompanies, getSubsidiaryData]
  );

  // Helper function to check if a parent has some (but not all) children selected
  const areSomeChildrenSelected = useCallback(
    (company) => {
      const subsidiaryData = getSubsidiaryData(company);

      if (!subsidiaryData || !Array.isArray(subsidiaryData)) {
        return false; // No children
      }

      return subsidiaryData.some((child) => {
        const isChildSelected = selectedCompanies.has(child.id);
        const areSomeChildChildrenSelected = areSomeChildrenSelected(child);
        return isChildSelected || areSomeChildChildrenSelected;
      });
    },
    [selectedCompanies, getSubsidiaryData]
  );

  // Function to fetch subsidiary data for a company
  const fetchSubsidiaryData = useCallback(async (companyId) => {
    try {
      const response = await axios.get(
        `${API_URL}/company/${companyId}/subsidiary/`
      );
      return response.data?.subsidiary || [];
    } catch (error) {
      console.error(
        `Failed to fetch subsidiary data for company ${companyId}:`,
        error
      );
      return [];
    }
  }, []);

  // Recursive function to fetch and select all descendants
  const fetchAndSelectDescendants = useCallback(
    async (company, isChecked) => {
      // First, handle the current company
      onSelectionChange(company.id, isChecked, company);

      // Try to get subsidiary data (either from cache or API)
      let subsidiaryData = getSubsidiaryData(company);

      // If no subsidiary data exists and we're checking, fetch it from API
      if (!subsidiaryData && isChecked) {
        subsidiaryData = await fetchSubsidiaryData(company.id);

        // Cache the fetched data
        if (subsidiaryData && subsidiaryData.length > 0) {
          setSubsidiaryDataCache((prev) => {
            const newCache = new Map(prev);
            newCache.set(company.id, subsidiaryData);
            return newCache;
          });
        }
      }

      // If we have subsidiary data, process each child recursively
      if (
        subsidiaryData &&
        Array.isArray(subsidiaryData) &&
        subsidiaryData.length > 0
      ) {
        // Process each child company recursively
        for (const childCompany of subsidiaryData) {
          await fetchAndSelectDescendants(childCompany, isChecked);
        }
      }
    },
    [
      onSelectionChange,
      getSubsidiaryData,
      fetchSubsidiaryData,
      setSubsidiaryDataCache,
    ]
  );

  const handleCheckboxChange = async (company, isChecked) => {
    if (!allowMultipleSelection) {
      // Single selection mode - just select this company
      onSelectionChange(company.id, isChecked, company);
      return;
    }

    // Multiple selection mode - handle hierarchical logic
    if (isChecked) {
      // Always select the company itself first
      onSelectionChange(company.id, true, company);

      // Only fetch descendants if hierarchical mode is enabled
      if (hierarchicalSelection) {
        await fetchAndSelectDescendants(company, true);
      }
    } else {
      // When unchecking, always uncheck the company itself
      onSelectionChange(company.id, false, company);

      // Only uncheck descendants if hierarchical mode is enabled
      if (hierarchicalSelection) {
        // Get all currently selected companies and find which ones are descendants of this company
        const allSelectedIds = Array.from(selectedCompanies);
        const descendantsToUncheck = [];

        // We need to recursively check if any selected company is a descendant
        const checkIfDescendant = async (parentId, targetIds) => {
          try {
            const subsidiaryData = await fetchSubsidiaryData(parentId);
            if (subsidiaryData && Array.isArray(subsidiaryData)) {
              for (const child of subsidiaryData) {
                if (targetIds.includes(child.id)) {
                  descendantsToUncheck.push(child);
                }
                // Recursively check this child's descendants
                await checkIfDescendant(child.id, targetIds);
              }
            }
          } catch (error) {
            console.error(`Error checking descendants for ${parentId}:`, error);
          }
        };

        // Check all selected companies to see if they're descendants of the unchecked company
        await checkIfDescendant(company.id, allSelectedIds);

        // Uncheck all found descendants
        descendantsToUncheck.forEach((descendant) => {
          onSelectionChange(descendant.id, false, descendant);
        });
      }
    }
  };

  const handleExpandClick = (e, companyId) => {
    e.preventDefault();
    e.stopPropagation();
    setExpandedItems((prev) => {
      const newMap = new Map(prev);
      newMap.set(companyId, !newMap.get(companyId));
      return newMap;
    });
  };

  // Get the data source based on level
  const dataSource = level === 0 ? rootData : children?.subsidiary;
  const isLoading = level === 0 ? false : isLoadingSubCompany;

  // Filter data based on search query (only for root level)
  const filteredData =
    level === 0 && searchQuery
      ? dataSource.filter((company) =>
          company.name.toLowerCase().includes(searchQuery.toLowerCase())
        )
      : dataSource;

  if (isLoading && expandedItems.get(parentId)) {
    return (
      <div
        className="flex gap-3 items-center p-3 text-gray-500"
        style={{ marginLeft: `${level * 20}px` }}
      >
        <div className="w-4 h-4 rounded-full border-2 border-blue-500 animate-spin border-t-transparent"></div>
        <span className="text-sm">Loading...</span>
      </div>
    );
  }

  if (!dataSource || dataSource?.length === 0) {
    return level === 0 ? (
      <div className="p-6 text-center text-gray-500">
        <i className="mb-2 text-2xl text-gray-300 ri-building-4-line"></i>
        <p className="text-sm font-medium">No companies found</p>
        <p className="mt-1 text-xs text-gray-400">Try adjusting your search</p>
      </div>
    ) : null;
  }

  return (
    <div style={{ marginLeft: `${level * 16}px` }}>
      {filteredData.map((company) => {
        const isSelected = selectedCompanies.has(company.id);
        const isExpanded = expandedItems.get(company.id);

        // For multiple selection mode, check parent-child relationships
        const subsidiaryForDisplay = getSubsidiaryData(company);
        const hasChildren =
          subsidiaryForDisplay &&
          Array.isArray(subsidiaryForDisplay) &&
          subsidiaryForDisplay.length > 0;

        let isChecked = isSelected;
        let isIndeterminate = false;

        if (allowMultipleSelection && hasChildren) {
          const allChildrenSelected = areAllChildrenSelected(company);
          const someChildrenSelected = areSomeChildrenSelected(company);

          // Parent is checked if: it's explicitly selected OR all children are selected
          isChecked = isSelected || allChildrenSelected;

          // Parent is indeterminate if: some (but not all) children are selected AND parent itself is not explicitly selected
          isIndeterminate =
            !isSelected && someChildrenSelected && !allChildrenSelected;
        }

        return (
          <div
            key={company.id}
            className={`group ${
              level > 0
                ? "border-l-2 border-gray-100 hover:border-blue-200"
                : ""
            } transition-all duration-200`}
          >
            {/* Company Row */}
            <div
              className={`flex items-center gap-3 p-3 rounded-lg transition-all duration-200 ${
                level === 0
                  ? "hover:bg-gradient-to-r hover:from-gray-50 hover:to-blue-50/30 border-b border-gray-100 last:border-b-0"
                  : "hover:bg-blue-50/40 ml-2 my-1"
              } ${
                isSelected || isIndeterminate ? "bg-blue-50/60 shadow-sm" : ""
              }`}
            >
              {/* Expand/Collapse Button */}
              <button
                onClick={(e) => handleExpandClick(e, company.id)}
                className="flex-shrink-0 p-1.5 rounded-md transition-all duration-200 hover:bg-white hover:shadow-sm group-hover:bg-white/80"
              >
                <i
                  className={`text-sm transition-all duration-300 ${
                    isExpanded
                      ? "text-blue-600 rotate-0 ri-arrow-down-s-line"
                      : "text-gray-400 ri-arrow-right-s-line group-hover:text-blue-500"
                  }`}
                ></i>
              </button>

              {/* Checkbox or Radio Button */}
              <div className="relative flex-shrink-0">
                {allowMultipleSelection ? (
                  // Checkbox for multiple selection
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={isChecked}
                      ref={(el) => {
                        if (el) {
                          el.indeterminate = isIndeterminate;
                        }
                      }}
                      onChange={(e) => {
                        e.preventDefault();
                        handleCheckboxChange(company, e.target.checked);
                      }}
                      className={`w-4 h-4 text-blue-600 rounded border-2 border-gray-300 transition-all duration-200 focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 hover:border-blue-400 ${
                        isIndeterminate ? "bg-blue-100 border-blue-400" : ""
                      }`}
                    />
                    {isChecked && !isIndeterminate && (
                      <div className="flex absolute inset-0 justify-center items-center pointer-events-none">
                        <i className="text-xs text-white ri-check-line"></i>
                      </div>
                    )}
                    {isIndeterminate && (
                      <div className="flex absolute inset-0 justify-center items-center pointer-events-none">
                        <div className="w-2 h-0.5 bg-blue-600 rounded-full"></div>
                      </div>
                    )}
                  </div>
                ) : (
                  // Radio button for single selection
                  <input
                    type="radio"
                    name="company-selection"
                    checked={isSelected}
                    onChange={(e) => {
                      e.preventDefault();
                      handleCheckboxChange(company, e.target.checked);
                    }}
                    className="w-4 h-4 text-blue-600 border-2 border-gray-300 transition-all duration-200 focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 hover:border-blue-400"
                  />
                )}
              </div>

              {/* Company Logo (for root level) */}
              {level === 0 && (
                <div className="relative flex-shrink-0 group">
                  {!!company?.logo ? (
                    <div className="overflow-hidden w-7 h-7 bg-white rounded-lg border-2 border-gray-200 shadow-sm transition-all duration-200 group-hover:shadow-md">
                      <img
                        className="object-cover w-full h-full"
                        alt="logo"
                        src={company?.logo}
                      />
                    </div>
                  ) : (
                    <div className="flex justify-center items-center w-7 h-7 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg shadow-sm transition-all duration-200 group-hover:shadow-md">
                      <i className="text-sm text-gray-600 ri-building-4-line"></i>
                    </div>
                  )}
                </div>
              )}

              {/* Company Name */}
              <div className="flex-1 min-w-0">
                <span
                  className={`text-sm block truncate transition-all duration-200 ${
                    level === 0
                      ? isSelected || isIndeterminate
                        ? "font-semibold text-blue-700"
                        : "font-medium text-gray-900 group-hover:text-gray-800"
                      : isSelected || isIndeterminate
                      ? "font-medium text-blue-700"
                      : "text-gray-700 group-hover:text-gray-800"
                  }`}
                >
                  {company.name}
                </span>

                {/* Subtitle for parent companies */}
                {level === 0 && (
                  <span className="block mt-0.5 text-xs text-gray-500">
                    {allowMultipleSelection
                      ? "Select multiple allowed"
                      : "Select one"}
                    {isIndeterminate && (
                      <span className="ml-2 text-yellow-600">(Partial)</span>
                    )}
                    {isChecked && !isIndeterminate && (
                      <span className="ml-2 text-green-600">(Selected)</span>
                    )}
                  </span>
                )}
              </div>

              {/* Status Indicator */}
              {(isSelected || isIndeterminate) && (
                <div className="flex-shrink-0">
                  <div
                    className={`w-2 h-2 rounded-full ${
                      isIndeterminate ? "bg-yellow-500" : "bg-blue-500"
                    } animate-pulse`}
                  ></div>
                </div>
              )}
            </div>

            {/* Recursive Children with Animation */}
            {isExpanded && (
              <div className="duration-300 animate-in slide-in-from-top-2">
                <CompanyAccordionItem
                  key={`${company.id}-level-${level + 1}`}
                  parentId={company.id}
                  level={level + 1}
                  selectedCompanies={selectedCompanies}
                  onSelectionChange={onSelectionChange}
                  rootData={rootData}
                  searchQuery={searchQuery}
                  allowMultipleSelection={allowMultipleSelection}
                  hierarchicalSelection={hierarchicalSelection}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const SelectSiteHeader = () => {
  const {
    dashboardId,
    plantId,
    companyId,
    selectedCompanies: storeSelectedCompanies,
    hierarchicalSelection,
    updateDashboardStates,
    updateSelectedCompanies,
    updateDashboardAndCompanies,
    updateHierarchicalSelection,
  } = useDempStore((state) => state);

  // console.table("table-lol", { plantId, companyId, storeSelectedCompanies });

  const { t } = useTranslation("main");
  const navigate = useNavigate();
  const location = useLocation();
  const { plantsList } = usePlantsList();

  // Check if current URL allows multiple company selection
  const allowMultipleSelection = useMemo(
    () =>
      location.pathname.includes("dap-service") ||
      location.pathname.includes("task-management") ||
      location.search.includes("map=dap") ||
      location.search.includes("map=risk"),
    [location.pathname, location.search]
  );

  // Track previous allowMultipleSelection state to detect transitions
  const prevAllowMultipleSelection = useRef(allowMultipleSelection);

  // Component state (declare early to avoid reference errors)
  const [selectedCompanies, setSelectedCompanies] = useState(
    new Set(storeSelectedCompanies || [])
  );
  const [selectedCompaniesData, setSelectedCompaniesData] = useState(new Map()); // Map to store company data
  const [searchQuery, setSearchQuery] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Plant selection state (only when single company selected)
  const [selectedPlant, setSelectedPlant] = useState(null);
  const [plantSearchQuery, setPlantSearchQuery] = useState("");
  const [isPlantDropdownOpen, setIsPlantDropdownOpen] = useState(false);

  // Refs for click outside
  const dropdownRef = useRef(null);
  const plantDropdownRef = useRef(null);

  // Function to fetch subsidiary data for transitions
  const fetchSubsidiaryDataForTransition = useCallback(async (companyId) => {
    try {
      const response = await axios.get(
        `${API_URL}/company/${companyId}/subsidiary/`
      );
      return response.data?.subsidiary || [];
    } catch (error) {
      console.error(
        `Failed to fetch subsidiary data for company ${companyId}:`,
        error
      );
      return [];
    }
  }, []);

  // Function to recursively get all descendant IDs for a company
  const getAllDescendantIdsForTransition = useCallback(
    async (companyId, processedIds = new Set()) => {
      // Prevent infinite loops
      if (processedIds.has(companyId)) return [];
      processedIds.add(companyId);

      const descendantIds = [];
      const subsidiaryData = await fetchSubsidiaryDataForTransition(companyId);

      if (subsidiaryData && Array.isArray(subsidiaryData)) {
        for (const child of subsidiaryData) {
          descendantIds.push(child.id);
          // Recursively get descendants of this child
          const childDescendants = await getAllDescendantIdsForTransition(
            child.id,
            processedIds
          );
          descendantIds.push(...childDescendants);
        }
      }

      return descendantIds;
    },
    [fetchSubsidiaryDataForTransition]
  );

  // Helper function to find top-level parent from selected companies
  const findTopLevelParent = useCallback(
    (selectedIds) => {
      // Look for a company that exists in plantsList (top-level companies)
      for (const companyId of selectedIds) {
        const company = plantsList?.find((c) => c.id === companyId);
        if (company) {
          return companyId;
        }
      }
      // Fallback to first selected if no top-level parent found
      return selectedIds[0] || null;
    },
    [plantsList]
  );

  // Helper function to expand selection with all descendants
  const expandSelectionWithDescendants = useCallback(
    async (parentId) => {
      try {
        const descendantIds = await getAllDescendantIdsForTransition(parentId);
        if (descendantIds.length > 0) {
          const expandedSelection = new Set([parentId, ...descendantIds]);
          setSelectedCompanies(expandedSelection);
          updateSelectedCompanies(Array.from(expandedSelection));
          return true;
        }
      } catch (error) {
        console.error("Error expanding selection:", error);
      }
      return false;
    },
    [getAllDescendantIdsForTransition, updateSelectedCompanies]
  );

  // Helper function to collapse to single parent
  const collapseToSingleParent = useCallback(
    (selectedIds) => {
      const topLevelParentId = findTopLevelParent(selectedIds);

      if (topLevelParentId) {
        // Update state
        setSelectedCompanies(new Set([topLevelParentId]));
        updateSelectedCompanies([topLevelParentId]);

        // Clear child companies from data map, keep only parent
        const newSelectedCompaniesData = new Map();
        if (selectedCompaniesData.has(topLevelParentId)) {
          newSelectedCompaniesData.set(
            topLevelParentId,
            selectedCompaniesData.get(topLevelParentId)
          );
        }
        setSelectedCompaniesData(newSelectedCompaniesData);

        return topLevelParentId;
      }
      return null;
    },
    [findTopLevelParent, updateSelectedCompanies, selectedCompaniesData]
  );

  // Helper function to find company by ID (consolidates duplicate lookup logic)
  const findCompanyById = useCallback(
    (companyId) => {
      if (!companyId) return null;

      // Check selectedCompaniesData map first (for child companies)
      const companyFromMap = selectedCompaniesData.get(companyId);
      if (companyFromMap) return companyFromMap;

      // Then check plantsList (for parent companies)
      return plantsList?.find((company) => company.id === companyId) || null;
    },
    [selectedCompaniesData, plantsList]
  );

  // Helper function to toggle global hierarchical selection mode
  const toggleHierarchicalMode = useCallback(async () => {
    const newMode = !hierarchicalSelection;

    // If we have selected companies, handle the transition
    if (selectedCompanies.size > 0 && allowMultipleSelection) {
      if (newMode === true) {
        // Switching to hierarchical mode - ask if they want to expand existing parent selections
        if (selectedCompanies.size === 1) {
          const selectedArray = Array.from(selectedCompanies);
          const parentId = selectedArray[0];
          const parentCompany = findCompanyById(parentId);

          if (parentCompany && plantsList?.find((p) => p.id === parentId)) {
            // This is a top-level parent company
            updateHierarchicalSelection(newMode);
            // Auto-expand to include children
            await expandSelectionWithDescendants(parentId);
            return;
          }
        }
      } else {
        // Switching to parent-only mode - ask if they want to collapse to parents only
        if (selectedCompanies.size > 1) {
          updateHierarchicalSelection(newMode);
          // Auto-collapse to parent only
          const selectedArray = Array.from(selectedCompanies);
          collapseToSingleParent(selectedArray);
          return;
        }
      }
    }

    // Just toggle the mode if no special handling needed
    updateHierarchicalSelection(newMode);
  }, [
    hierarchicalSelection,
    updateHierarchicalSelection,
    selectedCompanies,
    allowMultipleSelection,
    findCompanyById,
    plantsList,
    expandSelectionWithDescendants,
    collapseToSingleParent,
  ]);

  // Get current company for display
  const currentCompany = findCompanyById(companyId);

  // Get single selected company (reuses findCompanyById logic)
  const singleSelectedCompany = useMemo(() => {
    if (selectedCompanies.size !== 1) return null;
    const selectedId = Array.from(selectedCompanies)[0];
    return findCompanyById(selectedId);
  }, [selectedCompanies, findCompanyById]);

  const companyPlants = useMemo(
    () => singleSelectedCompany?.plants || [],
    [singleSelectedCompany]
  );

  const filteredPlants = useMemo(
    () =>
      companyPlants.filter((plant) =>
        plant.name.toLowerCase().includes(plantSearchQuery.toLowerCase())
      ),
    [companyPlants, plantSearchQuery]
  );

  // Initialize local state from store (only once on mount)
  useEffect(() => {
    if (storeSelectedCompanies && storeSelectedCompanies.length > 0) {
      setSelectedCompanies(new Set(storeSelectedCompanies));
    }
  }, []); // Only run once on mount

  // Initialize selectedPlant from store plantId on mount
  useEffect(() => {
    if (plantId && selectedCompanies.size === 1) {
      const selectedId = Array.from(selectedCompanies)[0];
      const company = findCompanyById(selectedId);

      if (company && company.plants?.length > 0) {
        const storedPlant = company.plants.find(
          (plant) => plant.id === plantId
        );
        if (
          storedPlant &&
          (!selectedPlant || selectedPlant.id !== storedPlant.id)
        ) {
          setSelectedPlant(storedPlant);
        }
      }
    }
  }, [plantId, selectedCompanies.size, findCompanyById]); // Run when these dependencies change

  // Simple effect to handle mode transitions
  useEffect(() => {
    const handleTransitions = async () => {
      const prevMode = prevAllowMultipleSelection.current;
      const currentMode = allowMultipleSelection;

      // Handle Multi → Single transition or Force collapse
      if (
        (prevMode === true &&
          currentMode === false &&
          selectedCompanies.size > 1) ||
        (currentMode === false && selectedCompanies.size > 1)
      ) {
        const selectedArray = Array.from(selectedCompanies);
        collapseToSingleParent(selectedArray);
      }

      // Handle Single → Multi transition or Force expand
      // Only auto-expand if hierarchical selection is enabled
      else if (
        hierarchicalSelection &&
        ((prevMode === false &&
          currentMode === true &&
          selectedCompanies.size === 1) ||
          (currentMode === true && selectedCompanies.size === 1))
      ) {
        const selectedArray = Array.from(selectedCompanies);
        const parentId = selectedArray[0];
        await expandSelectionWithDescendants(parentId);
      }

      // Update the ref for next comparison
      prevAllowMultipleSelection.current = currentMode;
    };

    handleTransitions();
  }, [
    allowMultipleSelection,
    selectedCompanies.size,
    plantsList,
    collapseToSingleParent,
    expandSelectionWithDescendants,
    hierarchicalSelection,
  ]);

  // Handle plant selection based on company selection changes
  useEffect(() => {
    if (selectedCompanies.size === 1) {
      // Single company selected
      const selectedId = Array.from(selectedCompanies)[0];
      const company = findCompanyById(selectedId);

      if (company && company.plants?.length > 0) {
        // Check if we should use existing plant from store or auto-select first plant
        let targetPlant = null;

        // If there's a stored plantId and it belongs to this company, use it
        if (plantId) {
          const storedPlant = company.plants.find(
            (plant) => plant.id === plantId
          );
          if (storedPlant) {
            targetPlant = storedPlant;
          }
        }

        // If no valid stored plant, fall back to first plant
        if (!targetPlant) {
          targetPlant = company.plants[0];
        }

        // Only update if plant has changed or no plant is selected
        if (!selectedPlant || selectedPlant.id !== targetPlant.id) {
          setSelectedPlant(targetPlant);

          // Update store with the target plant
          updateDashboardAndCompanies({
            dashboardId:
              targetPlant.dashboards?.[0]?.id ||
              company.dashboards?.[0]?.id ||
              "",
            type: company.type,
            companyId: selectedId,
            plantId: targetPlant.id,
            functions: company.function || [],
            hidden: company.hidden || [],
            selectedCompanies: Array.from(selectedCompanies),
          });
        }
      } else if (company) {
        // Company has no plants - clear plant selection
        setSelectedPlant(null);
        updateDashboardAndCompanies({
          dashboardId: company.dashboards?.[0]?.id || "",
          type: company.type,
          companyId: selectedId, // Use the actual selected company ID (parent or child)
          plantId: "",
          functions: company.function || [],
          hidden: company.hidden || [],
          selectedCompanies: Array.from(selectedCompanies),
        });
      }
    } else {
      // Multiple companies or no companies selected - clear plant selection
      setSelectedPlant(null);
      setPlantSearchQuery("");
      setIsPlantDropdownOpen(false);

      if (selectedCompanies.size > 1) {
        // Multiple companies - set plantId to null
        const selectedArray = Array.from(selectedCompanies);
        const primaryCompanyId = selectedArray[0];
        const primaryCompany = findCompanyById(primaryCompanyId);

        if (primaryCompany) {
          updateDashboardAndCompanies({
            dashboardId: primaryCompany.dashboards?.[0]?.id || "",
            type: primaryCompany.type,
            companyId: primaryCompanyId,
            plantId: null, // No plant for multiple selections
            functions: primaryCompany.function || [],
            hidden: primaryCompany.hidden || [],
            selectedCompanies: selectedArray,
          });
        }
      }
    }
  }, [
    selectedCompanies,
    findCompanyById,
    updateDashboardAndCompanies,
    plantId,
  ]);

  // Sync selectedCompanies with store (debounced)
  useEffect(() => {
    const currentSelectedArray = Array.from(selectedCompanies);
    const storeSelectedArray = storeSelectedCompanies || [];

    // Only update store if arrays are different
    if (
      JSON.stringify(currentSelectedArray.sort()) !==
      JSON.stringify(storeSelectedArray.sort())
    ) {
      const timeoutId = setTimeout(() => {
        updateSelectedCompanies(currentSelectedArray);
      }, 100); // Debounce store updates

      return () => clearTimeout(timeoutId);
    }
  }, [selectedCompanies, storeSelectedCompanies, updateSelectedCompanies]);

  // Handle company selection changes
  const handleCompanySelectionChange = useCallback(
    (companyId, isChecked, companyData) => {
      setSelectedCompanies((prev) => {
        const newSet = new Set(prev);

        // If not allowing multiple selection, clear existing selections first
        if (!allowMultipleSelection && isChecked) {
          newSet.clear();
        }

        if (isChecked) {
          newSet.add(companyId);
        } else {
          newSet.delete(companyId);
        }

        return newSet;
      });

      // Update the company data map
      setSelectedCompaniesData((prev) => {
        const newMap = new Map(prev);
        if (isChecked && companyData) {
          newMap.set(companyId, companyData);
        } else {
          newMap.delete(companyId);
        }
        return newMap;
      });
    },
    [allowMultipleSelection]
  );

  // Handle plant selection
  const handlePlantSelection = useCallback(
    (plant) => {
      setSelectedPlant(plant);
      setIsPlantDropdownOpen(false);
      setPlantSearchQuery("");

      // Update store with selected plant
      if (singleSelectedCompany) {
        updateDashboardAndCompanies({
          dashboardId:
            plant.dashboards?.[0]?.id ||
            singleSelectedCompany.dashboards?.[0]?.id ||
            "",
          type: singleSelectedCompany.type,
          companyId: singleSelectedCompany.id,
          plantId: plant.id,
          functions: singleSelectedCompany.function || [],
          hidden: singleSelectedCompany.hidden || [],
          selectedCompanies: Array.from(selectedCompanies),
        });
      }
    },
    [singleSelectedCompany, selectedCompanies, updateDashboardAndCompanies]
  );

  // Apply filters
  const handleApplyFilters = useCallback(() => {
    if (selectedCompanies.size > 0) {
      setIsDropdownOpen(false);
      navigate(location.pathname + location.search);
    }
  }, [selectedCompanies.size, navigate, location.pathname, location.search]);

  // Clear all selections
  const handleClearSelections = useCallback(() => {
    setSelectedCompanies(new Set());
    setSelectedPlant(null);

    updateDashboardAndCompanies({
      dashboardId: "",
      type: "",
      companyId: "",
      plantId: "",
      functions: [],
      hidden: [],
      selectedCompanies: [],
    });

    // Route back to home when clearing all selections
    navigate("/");
  }, [selectedCompanies.size, updateDashboardAndCompanies, navigate]);

  // Create simple display for selected companies
  const getSimpleSelectionDisplay = () => {
    if (selectedCompanies.size === 0) return null;

    if (selectedCompanies.size === 1) {
      const selectedId = Array.from(selectedCompanies)[0];
      const company =
        selectedCompaniesData.get(selectedId) ||
        plantsList?.find((p) => p.id === selectedId);
      return company ? company.name : `Company ${selectedId}`;
    }

    return `${selectedCompanies.size} companies selected`;
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
      if (
        plantDropdownRef.current &&
        !plantDropdownRef.current.contains(event.target)
      ) {
        setIsPlantDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="flex gap-3 items-center">
      {/* Company Selection Dropdown */}
      <div ref={dropdownRef} className="relative">
        <button
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          className={cn(
            "flex items-center gap-3 px-4 py-2.5 bg-white border-2 rounded-xl shadow-sm transition-all duration-200 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1",
            {
              "border-blue-400 shadow-md bg-blue-50/30": isDropdownOpen,
              "border-blue-300 bg-blue-50/20": selectedCompanies.size > 0,
              "border-gray-300 hover:border-gray-400":
                !isDropdownOpen && selectedCompanies.size === 0,
            }
          )}
        >
          {/* Current Company Logo */}
          <div className="flex-shrink-0">
            {currentCompany?.logo ? (
              <div className="overflow-hidden w-6 h-6 bg-white rounded-lg border border-gray-200 shadow-sm">
                <img
                  className="object-cover w-full h-full"
                  alt="logo"
                  src={currentCompany?.logo}
                />
              </div>
            ) : (
              <div className="flex justify-center items-center w-6 h-6 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg">
                <i className="text-sm text-gray-600 ri-building-4-line"></i>
              </div>
            )}
          </div>

          {/* Company Name and Info */}
          <div className="flex-1 min-w-0 text-left">
            {selectedCompanies.size > 0 ? (
              // Show simple selection when items are selected
              <div className="relative group">
                <span className="block text-sm font-medium text-blue-700 truncate cursor-pointer">
                  {getSimpleSelectionDisplay()}
                </span>
              </div>
            ) : (
              // Show current company when no selection
              <span className="block text-sm font-medium text-gray-900 truncate max-w-48">
                {currentCompany?.name || "Select Company"}
              </span>
            )}
          </div>

          {/* Selection Badge and Arrow */}
          <div className="flex gap-2 items-center">
            {selectedCompanies.size > 0 && (
              <span className="px-2 py-1 text-xs font-bold text-white bg-blue-500 rounded-full shadow-sm">
                {selectedCompanies.size}
              </span>
            )}

            {/* Dropdown Arrow */}
            <i
              className={`text-lg text-gray-500 transition-all duration-300 ${
                isDropdownOpen
                  ? "text-blue-600 rotate-0 ri-arrow-up-s-line"
                  : "ri-arrow-down-s-line"
              }`}
            ></i>
          </div>
        </button>

        {/* Dropdown Content */}
        {isDropdownOpen && (
          <div className="absolute z-50 top-full left-0 mt-3 w-[420px] bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden animate-in slide-in-from-top-2 duration-300">
            {/* Header with Selection Status */}
            {selectedCompanies.size > 0 && (
              <div className="flex justify-between items-center p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-100">
                <div className="flex gap-2 items-center">
                  <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                  <span className="text-sm font-medium text-blue-700">
                    {selectedCompanies.size} companies selected
                  </span>
                </div>
                <button
                  onClick={handleClearSelections}
                  className="text-sm font-medium text-red-600 transition-colors hover:text-red-700 hover:underline"
                  title={
                    selectedCompanies.size === 1
                      ? "Clear selection and return to home"
                      : "Clear all selections"
                  }
                >
                  {selectedCompanies.size === 1 ? (
                    <span className="flex gap-1 items-center">
                      Clear Selection
                    </span>
                  ) : (
                    "Clear all"
                  )}
                </button>
              </div>
            )}

            <div className="p-4">
              {/* Global Hierarchical Toggle (only in multi-select mode) */}
              {/* {allowMultipleSelection && (
                <div className="p-3 mb-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                  <div className="flex justify-between items-center">
                    <div className="flex gap-3 items-center">
                      <div className="flex justify-center items-center w-8 h-8 bg-blue-50 rounded-full">
                        <span className="text-lg">
                          {hierarchicalSelection ? "👥" : "👤"}
                        </span>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-gray-900">
                          {hierarchicalSelection
                            ? "Select with subsidiaries"
                            : "Select parent only"}
                        </h4>
                        <p className="mt-0.5 text-xs text-gray-500">
                          {hierarchicalSelection
                            ? "Auto-select all child companies"
                            : "Select only the parent company"}
                        </p>
                      </div>
                    </div>

                   // Simple Toggle Switch
                    <button
                      onClick={toggleHierarchicalMode}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                        hierarchicalSelection
                          ? "bg-vekin-primary"
                          : "bg-gray-200"
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                          hierarchicalSelection
                            ? "translate-x-6"
                            : "translate-x-1 border-vekin-primary"
                        }`}
                      />
                    </button>
                  </div>
                </div>
              )} */}

              {/* Search Input */}
              <div className="mb-4">
                <div className="relative">
                  <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                    <i className="text-gray-400 ri-search-line"></i>
                  </div>
                  <input
                    type="text"
                    placeholder="Search companies..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="py-3 pr-10 pl-10 w-full text-sm rounded-lg border border-gray-300 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50/50 hover:bg-white"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery("")}
                      className="absolute right-3 top-1/2 text-gray-400 transition-colors transform -translate-y-1/2 hover:text-gray-600"
                    >
                      <i className="ri-close-line"></i>
                    </button>
                  )}
                </div>
              </div>

              {/* Hierarchical Company Accordion */}
              <div className="overflow-auto max-h-80 bg-gradient-to-b to-white rounded-xl border border-gray-200 from-gray-50/50 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                <CompanyAccordionItem
                  parentId={null}
                  level={0}
                  selectedCompanies={selectedCompanies}
                  onSelectionChange={handleCompanySelectionChange}
                  rootData={plantsList}
                  searchQuery={searchQuery}
                  allowMultipleSelection={allowMultipleSelection}
                  hierarchicalSelection={hierarchicalSelection}
                />
              </div>
            </div>

            {/* Apply Button */}
            {selectedCompanies.size > 0 && (
              <div className="p-4 border-t border-gray-200 bg-gray-50/50">
                <button
                  onClick={handleApplyFilters}
                  className="w-full px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-medium rounded-lg hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 shadow-sm hover:shadow-md transform hover:scale-[1.02]"
                >
                  <span className="flex gap-2 justify-center items-center">
                    <i className="ri-check-line"></i>
                    Apply Selection ({selectedCompanies.size})
                  </span>
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Plant Selection Dropdown (only when single company selected) */}
      {singleSelectedCompany && (
        <div ref={plantDropdownRef} className="relative">
          {/* Plant Dropdown Trigger Button */}
          <button
            onClick={() => setIsPlantDropdownOpen(!isPlantDropdownOpen)}
            className={`flex items-center gap-3 px-4 py-2.5 bg-white border-2 rounded-xl shadow-sm transition-all duration-200 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-1 ${
              isPlantDropdownOpen
                ? "border-green-400 shadow-md bg-green-50/30"
                : selectedPlant
                ? "border-green-300 bg-green-50/20"
                : "border-gray-300 hover:border-gray-400"
            }`}
          >
            {/* Plant Icon */}
            <div className="flex-shrink-0">
              <div className="flex justify-center items-center w-6 h-6 bg-gradient-to-br from-green-100 to-green-200 rounded-lg">
                <i className="text-sm text-green-600 ri-plant-line"></i>
              </div>
            </div>

            {/* Plant Name */}
            <div className="flex-1 min-w-0 text-left">
              <span className="block text-sm font-medium text-gray-900 truncate max-w-48">
                {selectedPlant?.name || "Select Plant"}
              </span>
            </div>

            {/* Dropdown Arrow */}
            <i
              className={`text-lg text-gray-500 transition-all duration-300 ${
                isPlantDropdownOpen
                  ? "text-green-600 rotate-0 ri-arrow-up-s-line"
                  : "ri-arrow-down-s-line"
              }`}
            ></i>
          </button>

          {/* Plant Dropdown Content */}
          {isPlantDropdownOpen && (
            <div className="absolute z-50 top-full left-0 mt-3 w-[320px] bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden animate-in slide-in-from-top-2 duration-300">
              <div className="p-4">
                {/* Plant Search Input */}
                <div className="mb-4">
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                      <i className="text-gray-400 ri-search-line"></i>
                    </div>
                    <input
                      type="text"
                      placeholder="Search plants..."
                      value={plantSearchQuery}
                      onChange={(e) => setPlantSearchQuery(e.target.value)}
                      className="py-3 pr-10 pl-10 w-full text-sm rounded-lg border border-gray-300 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-gray-50/50 hover:bg-white"
                    />
                    {plantSearchQuery && (
                      <button
                        onClick={() => setPlantSearchQuery("")}
                        className="absolute right-3 top-1/2 text-gray-400 transition-colors transform -translate-y-1/2 hover:text-gray-600"
                      >
                        <i className="ri-close-line"></i>
                      </button>
                    )}
                  </div>
                </div>

                {/* Plants List */}
                <div className="overflow-auto max-h-60 bg-gradient-to-b to-white rounded-xl border border-gray-200 from-gray-50/50 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                  {filteredPlants?.length > 0 ? (
                    filteredPlants?.map((plant) => (
                      <div
                        key={plant.id}
                        className="flex gap-3 items-center p-3 border-b border-gray-100 transition-colors cursor-pointer hover:bg-green-50/60 last:border-b-0"
                        onClick={() => handlePlantSelection(plant)}
                      >
                        <div className="flex justify-center items-center w-5 h-5 bg-green-100 rounded-md">
                          <i className="text-xs text-green-600 ri-seedling-line"></i>
                        </div>
                        <span className="flex-1 text-sm text-gray-900 truncate">
                          {plant.name}
                        </span>
                        {selectedPlant?.id === plant.id && (
                          <i className="text-green-600 ri-check-line"></i>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="p-6 text-center text-gray-500">
                      <i className="mb-2 text-2xl text-gray-300 ri-plant-line"></i>
                      <p className="text-sm font-medium">No plants found</p>
                      <p className="mt-1 text-xs text-gray-400">
                        Try adjusting your search
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SelectSiteHeader;
